import { Common } from './common';
import { EhrInformation } from './models/ehrInformation';
import {
  ContactStatus,
  Patient,
  PhoneNumberStatus,
  Sex,
} from './models/patient';
import { Language } from './models/previsit/language';
import * as PatientNotification from './models/patient/notification';
import { PrevisitPharmacy } from './models/pharmacy';

export class API {
  common: Common;

  constructor(common: Common) {
    this.common = common;
  }

  async findAll(): Promise<Patient[]> {
    const url = this.common.constructPath('/patients');
    const { patients } = await this.common.getJSON(url);
    return patients;
  }

  async findOneById(id: string): Promise<Patient> {
    const url = this.common.constructPath(`/patients/${id}`);
    const { patient } = await this.common.getJSON(url);
    return patient;
  }

  async findOneByEhrValue(
    practiceId: string,
    value: string,
  ): Promise<{ patient: Patient; ehrInformation: EhrInformation }> {
    const url = this.common.constructPath(`/ehr-information/patient`, {
      practiceId,
      value,
    });
    return this.common.getJSON(url);
  }

  async create(patient: {
    firstName: string;
    lastName: string;
    dateOfBirth: Date;
    phoneNumber?: string;
    cellPhoneNumber?: string;
    homePhoneNumber?: string;
    workPhoneNumber?: string;
    sex?: Sex;
    ssn4?: string;
    email?: string;
    preferredLanguage?: Language;
  }): Promise<Patient> {
    const url = this.common.constructPath(`/patients/`);
    return this.common.postJSON(url, patient);
  }

  async createEhrInformation(ehrInformation: {
    practiceId: string;
    patientId: string;
    value: string;
    isTest?: boolean;
  }): Promise<EhrInformation> {
    const url = this.common.constructPath('/ehr-information/');
    return this.common.postJSON(url, ehrInformation);
  }

  async update(
    id: string,
    values: { dateOfBirth?: Date; firstName?: string; lastName?: string },
  ) {
    const url = this.common.constructPath(`/patients/${id}`);
    return this.common.putJSON(url, values);
  }

  async findEhrInformationByPatientId(
    patientId: string,
  ): Promise<EhrInformation[]> {
    const url = this.common.constructPath(
      `/patients/${patientId}/ehrInformation`,
    );
    const { ehrInformationList } = await this.common.getJSON(url);
    return ehrInformationList;
  }

  async findPatientPharmacies(): Promise<PrevisitPharmacy[]> {
    const url = this.common.constructPath(
      `/patient-previsit/patient-pharmacies`,
    );
    return this.common.getJSON(url);
  }

  async findContactStatusByPatientId(
    patientId: string,
  ): Promise<ContactStatus> {
    const url = this.common.constructPath(
      `/patients/${patientId}/contact-status`,
    );
    return this.common.getJSON(url);
  }

  async findStatusForPhoneNumber(
    number: string,
  ): Promise<{ number: string; status: PhoneNumberStatus }> {
    const url = this.common.constructPath('/patients/phone-number-status', {
      number,
    });
    return this.common.getJSON(url);
  }

  async upsertStatusForPhoneNumber(
    number: string,
    status: PhoneNumberStatus,
  ): Promise<{ number: string; status: PhoneNumberStatus }> {
    const url = this.common.constructPath('/patients/phone-number-status');
    return this.common.postJSON(url, { number, status });
  }

  async findStatusForEmail(
    email: string,
  ): Promise<{ email: string; subscribed: boolean }> {
    const url = this.common.constructPath('/patients/email-status', { email });
    return this.common.getJSON(url);
  }

  async upsertStatusForEmail(
    email: string,
    subscribed: boolean,
  ): Promise<{ success: boolean }> {
    const url = this.common.constructPath('/patients/email-status');
    return this.common.postJSON(url, { email, subscribed });
  }

  async fetchPatientNotifications(args: {
    patientId: string;
    encounterId?: string;
    previsitFormSetId?: string;
    source: PatientNotification.Source;
    includeContent?: boolean;
  }): Promise<PatientNotification.PatientNotification[]> {
    const {
      patientId,
      encounterId,
      previsitFormSetId,
      source,
      includeContent,
    } = args;
    const url = this.common.constructPath(
      `/patients/${patientId}/notifications`,
      {
        encounterId,
        previsitFormSetId,
        source,
        includeContent,
      },
    );
    return this.common.getJSON(url);
  }
}
